/**
 * DocumentSummaryScreen — summary of document choices after user clicks Continue from chat flow.
 * One block per document with selected status (green check) and edit icon.
 * Filter by the 3 choice types. Reuses ContinueBlock at the bottom.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { fetchChatFlow, type ChatFlowDocumentItem, type ChatFlowResponse } from '../../../../services/chatFlowApi';
import { allowsVariousCopySkip } from '../../../../catalog/variousCopyDocCodes';
import { getEmployeeEmail } from '../../auth/employeeAuthStorage';
import checkIcon from '../../../../assests/icons/check.png';
import editIcon from '../../../../assests/icons/edit_icon.png';
import filterIcon from '../../../../assests/icons/filter_icon.png';
import approveIcon from '../../../../assests/icons/approved.png';
import rejectIcon from '../../../../assests/icons/rejected.png';
import commentIcon from '../../../../assests/icons/comment.png';
import ContinueBlock from '../components/ContinueBlock';
import './DocumentSummaryScreen.css';

export type DocumentSummaryFilter = 'all' | 'submit_this_time' | 'send_later' | 'bring_when_boarding' | 'skipped';

export interface DocumentSummaryScreenProps {
  /** choices = original summary (選択肢表示). review = resubmission review list (承認/却下 + コメント + 編集). */
  mode?: 'choices' | 'review';
  onBack: () => void;
  /** Called when user taps the continue button at the bottom */
  onContinue: () => void;
  /** Called when user taps edit on a document block; opens edit chat flow */
  onEdit?: (doc: ChatFlowDocumentItem, invitationId: string) => void;
}

/** Backend status to display-option key (for check mark and label) */
const STATUS_OPTION_MAP: Record<string, 'submit_this_time' | 'send_later' | 'bring_when_boarding' | 'skipped'> = {
  submitted: 'submit_this_time',
  notyet: 'submit_this_time',
  send_later: 'send_later',
  bring_when_boarding: 'bring_when_boarding',
  skipped: 'skipped',
};

const OPTION_KEYS: ('submit_this_time' | 'send_later' | 'bring_when_boarding' | 'skipped')[] = [
  'submit_this_time',
  'send_later',
  'bring_when_boarding',
  'skipped',
];

/** Map option key to chat translation key for label */
const OPTION_LABEL_KEYS: Record<string, string> = {
  submit_this_time: 'submitThisTime',
  send_later: 'sendLater',
  bring_when_boarding: 'bringWhenBoarding',
  skipped: 'doNotHaveDocument',
};

/** Circled number ① ② ③ … (Unicode) */
function circledNumber(index: number): string {
  return String.fromCodePoint(0x2460 + index);
}

function matchesFilter(doc: ChatFlowDocumentItem, filter: DocumentSummaryFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'submit_this_time') return doc.status === 'submitted' || doc.status === 'notyet';
  if (filter === 'send_later') return doc.status === 'send_later';
  if (filter === 'bring_when_boarding') return doc.status === 'bring_when_boarding';
  if (filter === 'skipped') return doc.status === 'skipped';
  return true;
}

const DocumentSummaryScreen: React.FC<DocumentSummaryScreenProps> = ({ mode = 'choices', onBack, onContinue, onEdit }) => {
  const { t, language } = useLanguage();
  const [flow, setFlow] = useState<ChatFlowResponse | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [filter, setFilter] = useState<DocumentSummaryFilter>('all');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [commentDoc, setCommentDoc] = useState<ChatFlowDocumentItem | null>(null);
  const filterDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target as Node)) {
        setShowFilterDropdown(false);
      }
    };
    if (showFilterDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showFilterDropdown]);

  useEffect(() => {
    const email = getEmployeeEmail();
    if (!email) {
      setLoadState('error');
      return;
    }
    let cancelled = false;
    fetchChatFlow(email, language)
      .then((data) => {
        if (cancelled) return;
        if (data?.documents?.length) {
          setFlow(data);
          setLoadState('ready');
        } else {
          setFlow(data ?? null);
          setLoadState(data ? 'ready' : 'error');
        }
      })
      .catch(() => {
        if (!cancelled) setLoadState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [language]);

  const msg = (key: string) => t(`submitDocuments.chat.${key}`) || key;
  const summaryMsg = (key: string) => t(`submitDocuments.summary.${key}`) || key;
  const startLabel = t('submitDocuments.fillFormsChecklist.start');

  if (loadState === 'loading') {
    return (
      <div className="document-summary">
        <div className="document-summary__card">
          <p className="document-summary__loading">{msg('loading')}</p>
        </div>
      </div>
    );
  }

  if (loadState === 'error' || !flow) {
    return (
      <div className="document-summary">
        <div className="document-summary__card">
          <p className="document-summary__error">{msg('loadError')}</p>
          <button type="button" className="document-summary__back-btn" onClick={onBack}>
            {summaryMsg('cancelProcedure')}
          </button>
        </div>
      </div>
    );
  }

  const documents = flow.documents;
  const filteredDocuments =
    mode === 'review' ? documents : documents.filter((doc) => matchesFilter(doc, filter));

  const filterOptions: { value: DocumentSummaryFilter; labelKey: string }[] = [
    { value: 'all', labelKey: 'filterAll' },
    { value: 'submit_this_time', labelKey: 'submitThisTime' },
    { value: 'send_later', labelKey: 'sendLater' },
    { value: 'bring_when_boarding', labelKey: 'bringWhenBoarding' },
    { value: 'skipped', labelKey: 'doNotHaveDocument' },
  ];

  return (
    <div className="document-summary">
      <div className="document-summary__card">
        <header className="document-summary__top">
          {mode === 'choices' && (
            <div className="document-summary__filter-wrap" ref={filterDropdownRef}>
              <button
                type="button"
                className="document-summary__filter-btn"
                onClick={() => setShowFilterDropdown((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={showFilterDropdown}
                aria-label={summaryMsg('filter')}
              >
                <img src={filterIcon} alt="" />
              </button>
              {showFilterDropdown && (
                <div className="document-summary__filter-dropdown" role="listbox">
                  {filterOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      role="option"
                      aria-selected={filter === opt.value}
                      className={`document-summary__filter-option ${filter === opt.value ? 'document-summary__filter-option--active' : ''}`}
                      onClick={() => {
                        setFilter(opt.value);
                        setShowFilterDropdown(false);
                      }}
                    >
                      {opt.value === 'all' ? summaryMsg(opt.labelKey) : msg(opt.labelKey)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </header>

        <div className="document-summary__scroll">
          {filteredDocuments.length === 0 ? (
            <p className="document-summary__empty">
              {documents.length === 0 ? msg('noDocuments') : summaryMsg('filterEmpty')}
            </p>
          ) : (
            <ul className="document-summary__list" role="list">
              {filteredDocuments.map((doc: ChatFlowDocumentItem, index: number) => (
                <DocumentBlock
                  key={doc.docCode}
                  doc={doc}
                  index={index}
                  msg={msg}
                  mode={mode}
                  startLabel={startLabel}
                  onOpenComment={mode === 'review' ? () => setCommentDoc(doc) : undefined}
                  onEdit={flow.invitationId && onEdit ? () => onEdit(doc, flow.invitationId) : undefined}
                />
              ))}
            </ul>
          )}
        </div>

        {documents.length > 0 && (
          <footer className="document-summary__footer">
            <ContinueBlock
              line1={summaryMsg('doneMessage')}
              line2={summaryMsg('nextMessage')}
              buttonLabel={summaryMsg('continue')}
              onContinue={onContinue}
            />
          </footer>
        )}
      </div>

      {commentDoc && (
        <div className="document-summary__comment-popup" role="dialog" aria-modal="true" aria-labelledby="doc-comment-title">
          <div className="document-summary__comment-backdrop" onClick={() => setCommentDoc(null)} aria-hidden />
          <div className="document-summary__comment-box">
            <div className="document-summary__comment-header">
              <h3 id="doc-comment-title" className="document-summary__comment-title">
                {commentDoc.nameJa ?? commentDoc.name} — コメント
              </h3>
              <button type="button" className="document-summary__comment-close" onClick={() => setCommentDoc(null)} aria-label="閉じる">
                ×
              </button>
            </div>
            <div className="document-summary__comment-body">
              <p className="document-summary__comment-text">{commentDoc.adminComment}</p>
            </div>
            <div className="document-summary__comment-footer">
              <button type="button" className="document-summary__comment-ok" onClick={() => setCommentDoc(null)}>
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface DocumentBlockProps {
  doc: ChatFlowDocumentItem;
  index: number;
  msg: (key: string) => string;
  mode: 'choices' | 'review';
  startLabel: string;
  onOpenComment?: () => void;
  onEdit?: () => void;
}

/** Rejected in any uploaded round — stays true after a new upload while latest row is still pending review. */
function hadRejectionRound(doc: ChatFlowDocumentItem): boolean {
  return (doc.filesByRound ?? []).some((r) => r.adminApproved === false);
}

/**
 * Employee may edit when the latest round still needs or allows a choice change during resubmit,
 * or when admin requested resubmit on an upload.
 */
function showReviewEditActions(doc: ChatFlowDocumentItem): boolean {
  if (doc.adminApproved === true) return false;
  if (
    doc.status === 'notyet' ||
    doc.status === 'send_later' ||
    doc.status === 'bring_when_boarding' ||
    doc.status === 'skipped' ||
    doc.status === 'submitted'
  ) {
    return true;
  }
  return doc.adminApproved === false || hadRejectionRound(doc);
}

const DocumentBlock: React.FC<DocumentBlockProps> = ({ doc, index, msg, mode, startLabel, onOpenComment, onEdit }) => {
  const selectedKey = STATUS_OPTION_MAP[doc.status] ?? 'submit_this_time';
  const displayName = doc.name || doc.nameJa || doc.docCode || `Document ${index + 1}`;
  const isRejected = doc.adminApproved === false;
  const isApproved = doc.adminApproved === true;
  const showResubmitRow = showReviewEditActions(doc);
  const isFresh = doc.choiceRecorded === false;
  const commentText = doc.adminComment?.trim() ?? '';
  const optionKeys =
    allowsVariousCopySkip(doc.docCode) || doc.status === 'skipped'
      ? OPTION_KEYS
      : OPTION_KEYS.filter((key) => key !== 'skipped');

  return (
    <li className="document-summary__block">
      <div className="document-summary__block-inner">
        <div className="document-summary__block-title-bar">
          <span className="document-summary__block-num" aria-hidden>
            {circledNumber(index)}
          </span>
          <span className="document-summary__block-title">{displayName}</span>
          {mode === 'review' && (
            <span className="document-summary__review-icons" aria-hidden>
              {isApproved && <img src={approveIcon} alt="" className="document-summary__review-icon" />}
              {isRejected && <img src={rejectIcon} alt="" className="document-summary__review-icon" />}
            </span>
          )}
        </div>
        {/* In review mode we still show the choice rows (checklist-style), like the requested screenshot. */}
        <ul className="document-summary__options" role="list">
          {optionKeys.map((key) => (
            <li key={key} className="document-summary__option">
              <span
                className={`document-summary__option-box ${selectedKey === key ? 'document-summary__option-box--checked' : ''}`}
                aria-hidden
              >
                {selectedKey === key && (
                  <img src={checkIcon} alt="" className="document-summary__option-check" />
                )}
              </span>
              <span className="document-summary__option-label">{msg(OPTION_LABEL_KEYS[key] ?? key)}</span>
            </li>
          ))}
        </ul>
      </div>
      {mode === 'choices' ? (
        <button
          type="button"
          className="document-summary__block-edit"
          aria-label={msg('undo')}
          onClick={onEdit ?? undefined}
        >
          <img src={editIcon} alt="" />
        </button>
      ) : isFresh ? (
        <div className="document-summary__review-actions">
          <button
            type="button"
            className="document-summary__block-start"
            onClick={onEdit ?? undefined}
            disabled={!onEdit}
          >
            {startLabel}
          </button>
        </div>
      ) : (
        <div className="document-summary__review-actions" aria-hidden={!showResubmitRow}>
          {showResubmitRow && (
            <>
              {commentText !== '' && (
                <button
                  type="button"
                  className="document-summary__review-action-btn"
                  aria-label="コメントを見る"
                  onClick={onOpenComment}
                >
                  <img src={commentIcon} alt="" />
                </button>
              )}
              <button
                type="button"
                className="document-summary__review-action-btn"
                aria-label="修正する"
                onClick={onEdit ?? undefined}
                disabled={!onEdit}
              >
                <img src={editIcon} alt="" />
              </button>
            </>
          )}
        </div>
      )}
    </li>
  );
};

export default DocumentSummaryScreen;
